import { Headphones, Loader2, Sparkles, Users, Zap } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { BrandButton } from "@/components/brand/brand-button";
import { BrandTextarea } from "@/components/brand/brand-textarea";
import {
	AlertDialog,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import type { Subscription } from "@/hooks/use-subscription";
import type { SubscriptionTier } from "@/lib/plans";

const CANCELLATION_REASONS = [
	"Too expensive",
	"Not using it enough",
	"Missing features I need",
	"Switching to another tool",
	"Other",
] as const;

type CancellationReason = (typeof CANCELLATION_REASONS)[number];

interface CancelSubscriptionDialogProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	subscription: Subscription;
	tier: SubscriptionTier;
	onCancelled: () => void;
}

export function CancelSubscriptionDialog({
	open,
	onOpenChange,
	subscription,
	tier,
	onCancelled,
}: CancelSubscriptionDialogProps) {
	const [step, setStep] = useState<"reason" | "retain">("reason");
	const [selectedReason, setSelectedReason] =
		useState<CancellationReason | null>(null);
	const [details, setDetails] = useState("");
	const [isCancelling, setIsCancelling] = useState(false);

	const handleOpenChange = (value: boolean) => {
		if (!value) {
			setStep("reason");
			setSelectedReason(null);
			setDetails("");
		}
		onOpenChange(value);
	};

	const handleContinue = () => {
		if (!selectedReason) return;
		setStep("retain");
	};

	const handleCancel = async () => {
		if (!selectedReason) return;
		setIsCancelling(true);

		try {
			const reason =
				selectedReason === "Other" && details
					? `Other: ${details}`
					: selectedReason;

			const res = await fetch("/api/stripe/cancel-subscription", {
				method: "POST",
				credentials: "include",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({ reason }),
			});

			const data = await res.json();
			if (!res.ok) {
				throw new Error(data.error || "Failed to cancel subscription");
			}

			toast.success("Your subscription has been cancelled", {
				description: subscription.periodEnd
					? `You'll have access until ${subscription.periodEnd.toLocaleDateString()}`
					: undefined,
			});
			handleOpenChange(false);
			onCancelled();
		} catch (error) {
			toast.error(
				error instanceof Error
					? error.message
					: "Failed to cancel subscription",
			);
		} finally {
			setIsCancelling(false);
		}
	};

	const usps = [
		{
			icon: Sparkles,
			title: "AI-powered content generation",
			description: "Generate videos & images in seconds",
		},
		{
			icon: Users,
			title: "Multi-account scheduling",
			description:
				tier === "max"
					? "Manage up to 100 social accounts"
					: "Manage up to 15 social accounts",
		},
		{
			icon: Zap,
			title: "Higher scheduling capacity",
			description: "Keep more posts queued across all your accounts",
		},
		...(tier === "max"
			? [
					{
						icon: Headphones,
						title: "Priority support",
						description: "We're here to help when you need it",
					},
				]
			: []),
	];

	return (
		<AlertDialog open={open} onOpenChange={handleOpenChange}>
			<AlertDialogContent className="max-w-md">
				{step === "reason" ? (
					<>
						<AlertDialogHeader>
							<AlertDialogTitle>We're sorry to see you go</AlertDialogTitle>
							<AlertDialogDescription>
								Help us improve by sharing why you're leaving.
							</AlertDialogDescription>
						</AlertDialogHeader>

						<div className="space-y-2 py-2">
							{CANCELLATION_REASONS.map((reason) => (
								<button
									key={reason}
									type="button"
									onClick={() => setSelectedReason(reason)}
									className={`w-full text-left px-4 py-3 rounded-lg border text-sm transition-colors ${
										selectedReason === reason
											? "border-primary bg-primary/5 text-foreground"
											: "border-border bg-card text-muted-foreground hover:border-primary/40 hover:text-foreground"
									}`}
								>
									{reason}
								</button>
							))}

							{selectedReason === "Other" && (
								<BrandTextarea
									placeholder="Tell us more..."
									value={details}
									onChange={(e) => setDetails(e.target.value)}
									minHeight={60}
									maxHeight={120}
									className="mt-2"
								/>
							)}
						</div>

						<AlertDialogFooter>
							<AlertDialogCancel>Cancel</AlertDialogCancel>
							<BrandButton
								size="sm"
								variant="secondary"
								disabled={!selectedReason}
								onClick={handleContinue}
							>
								Continue
							</BrandButton>
						</AlertDialogFooter>
					</>
				) : (
					<>
						<AlertDialogHeader>
							<AlertDialogTitle>
								Before you go, consider what you'll lose
							</AlertDialogTitle>
						</AlertDialogHeader>

						<div className="space-y-3 py-2">
							{usps.map((usp) => (
								<div
									key={usp.title}
									className="flex items-start gap-3 p-3 rounded-lg border border-primary/20 bg-primary/5"
								>
									<div className="mt-0.5 p-1.5 rounded-md bg-primary/10">
										<usp.icon className="w-4 h-4 text-primary" />
									</div>
									<div>
										<p className="text-sm font-medium text-foreground">
											{usp.title}
										</p>
										<p className="text-xs text-muted-foreground">
											{usp.description}
										</p>
									</div>
								</div>
							))}
						</div>

						{subscription.periodEnd && (
							<p className="text-xs text-muted-foreground text-center">
								Your subscription stays active until{" "}
								{subscription.periodEnd.toLocaleDateString()}
							</p>
						)}

						<AlertDialogFooter>
							<BrandButton
								variant="destructive"
								size="sm"
								onClick={handleCancel}
								disabled={isCancelling}
							>
								{isCancelling ? (
									<>
										<Loader2 className="w-4 h-4 animate-spin" />
										Cancelling...
									</>
								) : (
									"Cancel subscription"
								)}
							</BrandButton>
							<BrandButton size="sm" onClick={() => handleOpenChange(false)}>
								Keep my subscription
							</BrandButton>
						</AlertDialogFooter>
					</>
				)}
			</AlertDialogContent>
		</AlertDialog>
	);
}
